import React, { FC, useRef } from 'react';
import { Dialog, SwipeableDrawer, useMediaQuery } from '@mui/material';
import { isIOS } from 'react-device-detect';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useCookies } from 'react-cookie';
import { TopUpMethods } from './TopUpMethods';
import { EditEmail } from './accountSettings/account/EditEmail';
import { CookieItems, ModalVariants } from '../../../models/Common';
import { ConfirmationModal } from './ConfirmationModal';
import { SuccessModal } from './SuccessModal';
import { ModalContent } from '../../../store/reducers/ModalSlice';
import { AddNewEmail } from './accountSettings/account/AddNewEmail';
import { EditBackupPhone } from './accountSettings/account/EditBackupPhone';
import { AddNewBackupPhone } from './accountSettings/account/AddNewBackupPhone';
import { DeleteBackupPhone } from './accountSettings/account/DeleteBackupPhone';
import { AddNewSmsPhone } from './accountSettings/account/AddNewSmsPhone';
import { DeleteSmsPhone } from './accountSettings/account/DeleteSmsPhone';
import { ConfirmNewSmsPhone } from './accountSettings/account/ConfirmNewSmsPhone';
import { EditInternetLogin } from './internetSettings/EditInternetLogin';
import { EditInternetMacAddress } from './internetSettings/EditInternetMacAddress';
import { EditInternetPassword } from './internetSettings/EditInternetPassword';
import { EditIptvDevice } from './iptvDevice/EditIptvDevice';
import { AddNewIptvDevice } from './iptvDevice/AddNewIptvDevice';
import { DeleteIptvDevice } from './iptvDevice/DeleteIptvDevice';
import { EditBrizTvEmail } from './brizTvSettings/EditBrizTvEmail';
import { EditBrizTvPhone } from './brizTvSettings/EditBrizTvPhone';
import { EditBrizTvPassword } from './brizTvSettings/EditBrizTvPassword/EditBrizTvPassword';
import { DeleteCard } from './accountSettings/autoReplenishment/DeleteCard';
import { AddNewCard } from './accountSettings/autoReplenishment/AddNewCard';
import { Callback } from './Callback';
import { TopUpWarning } from './TopUpWarning';
import { RestoreCallback } from './RestoreCallback';
import { AnonymousFeedback } from './AnonymousFeedback';
import { LinkAccount } from './LinkAccount';
import { DeleteAccount } from './DeleteAccount';
import { LeaveAccount } from './LeaveAccount';
import { LogOut } from './LogOut';
import { AcceptMembership } from './AcceptMembership';
import { ConfirmationsLimit } from './ConfirmationsLimit';
import { DeleteAutoReplenishmentEmail } from './accountSettings/autoReplenishment/DeleteAutoReplenishmentEmail';
import { ConnectionProtocolTypes } from '../../../models/Accounts';
import { CheckRouterCapability } from './CheckRouterCapability';
import { ServiceSuspensionAlert } from './ServiceSuspension';
import { UnionCtvSuspension } from './UnionCtvSuspension';
import { InterviewRegistration } from './InterviewRegistration';
import { ProductTourStart } from './ProductTourStart';
import { ProductTourEnd } from './ProductTourEnd';
import { SubAccountSettings } from './SubAccountSettings';
import { PromoShareThreeMonths } from './PromoShareThreeMonths';
import { defaultSharePromo3Month } from '../../servicePaymentParts/SharePromoBRIZTVPremium';
import { PaymentTypeMethods } from './PaymentTypeMethods';
import { SelectAccount } from './SelectAccount';
import { defaultParamsForCalculation } from '../../../models/DefaultState';
import { DeleteEmail } from './accountSettings/account/DeleteEmail';
import { EditSmsPhone } from './accountSettings/account/EditSmsPhone';
import { GetHelp } from './GetHelp';

interface CustomModalProps {
  isOpen: boolean;
  handleClose: () => void;
  variant: ModalVariants;
  modalContent: ModalContent | null;
}

export const ParentModalWindow: FC<CustomModalProps> = ({ isOpen, handleClose, variant, modalContent }) => {
  const fullScreen = useMediaQuery('(max-width:767px)');
  const [, setCookie] = useCookies();
  const blockRef = useRef<HTMLDivElement>(null);
  const hasFullScreenMode = ![ModalVariants.ProductTourStart, ModalVariants.ProductTourEnd].includes(variant);
  const isSelectAccount = variant === ModalVariants.SelectAccount;

  const getModal = (type: ModalVariants) => {
    switch (type) {
      case ModalVariants.AddNewCard: {
        return <AddNewCard handleClose={handleClose} />;
      }
      case ModalVariants.AddNewEmail: {
        return <AddNewEmail />;
      }
      case ModalVariants.DeleteEmail: {
        return <DeleteEmail handleClose={handleClose} email={modalContent?.email || ''} />;
      }
      case ModalVariants.AddNewBackupPhone: {
        return <AddNewBackupPhone handleClose={handleClose} />;
      }
      case ModalVariants.AddNewSmsPhone: {
        return <AddNewSmsPhone modalTitle={modalContent?.title || ''} description={modalContent?.description} />;
      }
      case ModalVariants.EditSmsPhone: {
        return <EditSmsPhone modalTitle={modalContent?.title || ''} description={modalContent?.description} />;
      }
      case ModalVariants.AddNewIptvDevice: {
        return <AddNewIptvDevice handleClose={handleClose} accountId={modalContent?.accountId || 0} />;
      }
      case ModalVariants.Callback: {
        return (
          <Callback
            title={modalContent?.title}
            description={modalContent?.description}
            isGigabitPromo={modalContent?.isGigabitPromo}
            service={modalContent?.service}
            comment={modalContent?.comment}
            serviceAnalyticsName={modalContent?.serviceAnalyticsName}
            bannerId={modalContent?.userId}
          />
        );
      }
      case ModalVariants.GetHelp: {
        return <GetHelp service={modalContent?.service} />;
      }
      case ModalVariants.RestoreCallback: {
        return <RestoreCallback service={modalContent?.service} accountType={modalContent?.accountType} />;
      }
      case ModalVariants.AnonymousFeedback: {
        return <AnonymousFeedback />;
      }
      case ModalVariants.LinkAccount: {
        return <LinkAccount />;
      }
      case ModalVariants.ConfirmNewSmsPhone: {
        return <ConfirmNewSmsPhone phoneNumber={modalContent?.phone || ''} handleClose={handleClose} />;
      }
      case ModalVariants.DeleteSmsPhone: {
        return <DeleteSmsPhone handleClose={handleClose} phoneNumber={modalContent?.phone || ''} />;
      }
      case ModalVariants.DeleteBackupPhone: {
        return (
          <DeleteBackupPhone
            phoneNumber={modalContent?.phone || ''}
            phoneId={modalContent?.phoneId || 1}
            handleClose={handleClose}
          />
        );
      }
      case ModalVariants.DeleteIptvDevice: {
        return (
          <DeleteIptvDevice
            macAddress={modalContent?.macAddress || ''}
            deviceId={modalContent?.deviceId || 0}
            accountId={modalContent?.accountId || 0}
            handleClose={handleClose}
          />
        );
      }
      case ModalVariants.DeleteCard: {
        return (
          <DeleteCard
            cardNumber={modalContent?.cardNumber}
            cardId={modalContent?.cardId}
            isActiveCard={modalContent?.isActiveCard}
            handleClose={handleClose}
          />
        );
      }
      case ModalVariants.EditEmail: {
        return <EditEmail />;
      }
      case ModalVariants.EditInternetLogin: {
        return (
          <EditInternetLogin
            handleClose={handleClose}
            inputLogin={modalContent?.login || ''}
            accountId={modalContent?.accountId || 0}
            connectionProtocolId={modalContent?.connectionProtocolId || ConnectionProtocolTypes.Default}
            selectedLanguage={modalContent?.selectedLanguage || 'ua'}
          />
        );
      }
      case ModalVariants.EditMacAddress: {
        return (
          <EditInternetMacAddress
            handleClose={handleClose}
            accountId={modalContent?.accountId || 0}
            mac={modalContent?.macAddress || ''}
            connectionProtocolId={modalContent?.connectionProtocolId || ConnectionProtocolTypes.Default}
            selectedLanguage={modalContent?.selectedLanguage || 'ua'}
          />
        );
      }
      case ModalVariants.EditInternetPassword: {
        return (
          <EditInternetPassword
            handleClose={handleClose}
            accountId={modalContent?.accountId || 0}
            connectionProtocolId={modalContent?.connectionProtocolId || ConnectionProtocolTypes.Default}
            selectedLanguage={modalContent?.selectedLanguage || 'ua'}
          />
        );
      }
      case ModalVariants.EditIptvDevice: {
        return (
          <EditIptvDevice
            handleClose={handleClose}
            accountId={modalContent?.accountId || 0}
            login={modalContent?.login || ''}
            deviceId={modalContent?.deviceId || 0}
            macAddress={modalContent?.macAddress || ''}
          />
        );
      }
      case ModalVariants.EditBackupPhone: {
        return (
          <EditBackupPhone
            handleClose={handleClose}
            phone={modalContent?.phone || ''}
            id={modalContent?.phoneId || 1}
          />
        );
      }
      case ModalVariants.EditBrizTvEmail: {
        return <EditBrizTvEmail handleClose={handleClose} accountId={modalContent?.accountId || 0} />;
      }
      case ModalVariants.EditBrizTvPhone: {
        return <EditBrizTvPhone handleClose={handleClose} accountId={modalContent?.accountId || 0} />;
      }
      case ModalVariants.EditBrizTvPassword: {
        return <EditBrizTvPassword handleClose={handleClose} accountId={modalContent?.accountId || 0} />;
      }
      case ModalVariants.PayMethods: {
        return <TopUpMethods onClose={handleClose} />;
      }
      case ModalVariants.Success: {
        return <SuccessModal handleClose={handleClose} modalContent={modalContent} />;
      }
      case ModalVariants.Confirmation: {
        return (
          <ConfirmationModal
            handleProceed={modalContent?.handleProceed || (() => undefined)}
            handleClose={handleClose}
            notice={modalContent?.notice || ''}
          />
        );
      }
      case ModalVariants.ConfirmationsLimit: {
        return <ConfirmationsLimit handleClose={handleClose} notice={modalContent?.notice || ''} />;
      }
      case ModalVariants.DeleteAutoReplenishmentEmail: {
        return <DeleteAutoReplenishmentEmail handleClose={handleClose} />;
      }
      case ModalVariants.ProductTourStart: {
        return (
          <ProductTourStart
            handleProceed={modalContent?.handleProceed || (() => undefined)}
            userId={modalContent?.userId || 0}
            handleClose={handleClose}
          />
        );
      }
      case ModalVariants.SubAccountSettings: {
        return <SubAccountSettings userId={modalContent?.userId || 0} handleClose={handleClose} />;
      }
      case ModalVariants.ProductTourEnd: {
        return (
          <ProductTourEnd handleProceed={modalContent?.handleProceed || (() => undefined)} handleClose={handleClose} />
        );
      }
      case ModalVariants.CheckRouter: {
        return (
          <CheckRouterCapability
            isDaily={modalContent?.isDaily || false}
            search={modalContent?.search}
            title={modalContent?.title}
            handleClose={handleClose}
          />
        );
      }
      case ModalVariants.ServiceSuspension: {
        return (
          <ServiceSuspensionAlert
            title={modalContent?.title}
            service={modalContent?.service}
            notice={modalContent?.notice}
            handleProceed={modalContent?.handleProceed || (() => undefined)}
            handleClose={handleClose}
          />
        );
      }
      case ModalVariants.UnionCtvSuspension: {
        return (
          <UnionCtvSuspension
            title={modalContent?.title}
            service={modalContent?.service}
            handleProceed={modalContent?.handleProceed || (() => undefined)}
            handleClose={handleClose}
          />
        );
      }
      case ModalVariants.TopUpWarning: {
        return <TopUpWarning userId={modalContent?.accountId} handleClose={handleClose} />;
      }
      case ModalVariants.DeleteAccount: {
        return <DeleteAccount userId={modalContent?.accountId} handleClose={handleClose} />;
      }
      case ModalVariants.LeaveAccount: {
        return <LeaveAccount userId={modalContent?.accountId} handleClose={handleClose} />;
      }
      case ModalVariants.LogOut: {
        return <LogOut handleClose={handleClose} />;
      }
      case ModalVariants.AcceptMembership: {
        return <AcceptMembership handleClose={handleClose} />;
      }
      case ModalVariants.InterviewRegistration: {
        return <InterviewRegistration handleClose={handleClose} userId={modalContent?.userId || 0} />;
      }
      case ModalVariants.PromoShareThreeMonths: {
        return (
          <PromoShareThreeMonths
            shareCalculation={modalContent?.shareCalculation || defaultSharePromo3Month}
            handleActivate={modalContent?.handleActivate}
            handlePay={modalContent?.handlePay}
            handleClose={handleClose}
            customShareName={modalContent?.customShareName || ''}
            buttonShareName={modalContent?.buttonShareName || ''}
          />
        );
      }
      case ModalVariants.SelectAccount: {
        return <SelectAccount userId={modalContent?.accountId} handleClose={handleClose} />;
      }
      case ModalVariants.PaymentTypeMethods: {
        return (
          <PaymentTypeMethods
            currentCalculation={modalContent?.currentCalculation || null}
            paramsForCalculation={modalContent?.paramsForCalculation || defaultParamsForCalculation}
            serviceName={modalContent?.service || ''}
            topUpAmount={modalContent?.amount || 1}
            formTarget="payForService"
            refScrollToTop={blockRef}
            bannerId={modalContent?.userId || 0}
          />
        );
      }
      case ModalVariants.TopUpTypeMethods: {
        return (
          <PaymentTypeMethods
            currentCalculation={modalContent?.currentCalculation || null}
            paramsForCalculation={modalContent?.paramsForCalculation || defaultParamsForCalculation}
            serviceName={modalContent?.service || ''}
            topUpAmount={modalContent?.amount || 1}
            formTarget="topUpBalance"
            refScrollToTop={blockRef}
            bannerId={modalContent?.userId || 0}
          />
        );
      }
      default: {
        return <p>please select modal variant</p>;
      }
    }
  };

  const setCookieAndCloseWindow = () => {
    setCookie(`${CookieItems.INTERVIEW}_${modalContent?.userId}`, modalContent?.userId, { path: '/', maxAge: 86400 });
    handleClose();
  };

  const handleCloseProductTour = () => {
    setCookie(`${CookieItems.PRODUCT_TOUR}_${modalContent?.userId}`, modalContent?.userId, {
      path: '/',
      maxAge: 86400 * 90,
    });
    if (modalContent?.handleProceed) {
      modalContent?.handleProceed(true);
    }
    handleClose();
  };

  const getModalClassName = (type: ModalVariants) => {
    switch (type) {
      case ModalVariants.ProductTourStart:
      case ModalVariants.ProductTourEnd: {
        return 'mobile-default-screen';
      }
      case ModalVariants.SubAccountSettings: {
        return 'account-settings-screen';
      }
      default: {
        return '';
      }
    }
  };

  const getCloseHandler = (type: ModalVariants) => {
    switch (type) {
      case ModalVariants.InterviewRegistration: {
        return setCookieAndCloseWindow;
      }
      case ModalVariants.ProductTourStart: {
        return handleCloseProductTour;
      }
      default: {
        return handleClose;
      }
    }
  };

  if (isSelectAccount && fullScreen) {
    return (
      <SwipeableDrawer
        open={isOpen}
        onClose={handleClose}
        className="parent-modal-window mobile-bottom-screen"
        onOpen={() => null}
        anchor="bottom"
      >
        <div className="parent-modal-window__modal">
          <div className="modals__handle">
            <div className="modals__handle-pull" />
          </div>
          {getModal(variant)}
        </div>
      </SwipeableDrawer>
    );
  }

  return modalContent?.isShare ? (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      className="parent-modal-window share"
      fullScreen={hasFullScreenMode && fullScreen}
      maxWidth="xl"
      disableAutoFocus={hasFullScreenMode && fullScreen}
      disableRestoreFocus={hasFullScreenMode && fullScreen}
      disableScrollLock={hasFullScreenMode && fullScreen}
      scroll={isIOS ? 'body' : 'paper'}
    >
      <div className="parent-modal-window__close-button share">
        <IconButton onClick={handleClose} aria-label="close" size="small">
          <CloseIcon />
        </IconButton>
      </div>
      <div className="parent-modal-window__modal">{getModal(variant)}</div>
    </Dialog>
  ) : (
    <Dialog
      open={isOpen}
      onClose={getCloseHandler(variant)}
      className={`parent-modal-window ${getModalClassName(variant)}`}
      fullScreen={hasFullScreenMode && fullScreen}
      maxWidth="xl"
      disableAutoFocus={hasFullScreenMode && fullScreen}
      disableRestoreFocus={hasFullScreenMode && fullScreen}
      disableScrollLock={hasFullScreenMode && fullScreen}
      scroll={isIOS ? 'body' : 'paper'}
    >
      <div className="parent-modal-window__close-button" ref={blockRef}>
        <IconButton onClick={getCloseHandler(variant)} aria-label="close" size="small">
          <CloseIcon />
        </IconButton>
      </div>
      <div className="parent-modal-window__modal">{getModal(variant)}</div>
    </Dialog>
  );
};
